/**
 * Utility functions for restaurant access control and validation
 */

import { httpClient } from '@/src/@core/lib/httpClient'

/**
 * Fetches active delegations for a specific delegatee user
 * @param delegateeUserId - The ID of the delegatee user
 * @returns Array of delegation objects
 */
const fetchUserDelegations = async (delegateeUserId: number): Promise<any[]> => {
  try {
    const response = await httpClient.get('/resto365-role-delegations/active', {
      params: {
        delegateeUserId: delegateeUserId.toString(),
        page: '1',
        pageSize: '1000' // Get all delegations
      }
    })

    return response.data?.data?.delegations || []
  } catch {
    return []
  }
}

/**
 * Safely extracts the restaurants array from a user object.
 * Handles different data structures: array, object with data property, or null/undefined.
 *
 * @param user - The user object containing restaurant assignments
 * @returns An array of restaurant objects, or empty array if none found
 *
 * @example
 * ```typescript
 * const user = { restaurants: { data: [{ id: 1 }, { id: 2 }] } }
 * const restaurants = getUserRestaurants(user) // Returns [{ id: 1 }, { id: 2 }]
 * ```
 */
export const getUserRestaurants = (user: any): any[] => {
  if (!user || !user.restaurants) {
    return []
  }

  return Array.isArray(user.restaurants) ? user.restaurants : (user.restaurants as any)?.data || []
}

/**
 * Checks if a user has access to a specific restaurant based on their role scope.
 *
 * Business Rules:
 * - If user has 'restaurant' scope: user can ONLY access restaurants listed in their restaurants object OR delegated restaurants
 * - If user has any other scope (e.g., 'country'): user has access to ALL restaurants
 *
 * @param user - The authenticated user object
 * @param restaurantId - The restaurant ID to check access for (must be a number)
 * @returns Promise<boolean> - true if user has access, false otherwise
 *
 * @example
 * ```typescript
 * const user = {
 *   role: { scope: 'restaurant' },
 *   restaurants: [{ id: 1 }, { id: 2 }]
 * }
 * const hasAccess = await checkRestaurantAccess(user, 1) // Returns true
 * const noAccess = await checkRestaurantAccess(user, 3) // Returns false (unless delegated)
 * ```
 */
export const checkRestaurantAccess = async (user: any, restaurantId: number): Promise<boolean> => {
  if (!user || !restaurantId) {
    return false
  }

  const userRoleScope = user.role?.scope

  // If user has restaurant scope, they can ONLY access restaurants listed in their restaurants object OR delegated restaurants
  if (userRoleScope === 'restaurant') {
    // First check direct restaurant access
    const userRestaurants = getUserRestaurants(user)
    const hasDirectAccess = userRestaurants.some((restaurant: any) => {
      const restaurantIdFromUser = restaurant.id || restaurant.restaurantId

      return Number(restaurantIdFromUser) === restaurantId
    })

    if (hasDirectAccess) {
      return true
    }

    // If no direct access, check for delegated access
    if (user.id) {
      try {
        const delegations = await fetchUserDelegations(user.id)
        const hasDelegatedAccess = delegations.some((delegation: any) => {
          const delegationRestaurantId = Number(delegation.restaurant?.id || delegation.restaurantId)

          return delegationRestaurantId === restaurantId
        })

        return hasDelegatedAccess
      } catch {
        return false
      }
    }

    return false
  }

  // For any other scope (e.g., 'country'), user has access to all restaurants
  return true
}

/**
 * Validates and normalizes a restaurant ID from router query parameters.
 * Handles array, string, or undefined values and ensures strict numeric validation.
 *
 * @param restaurantIdRaw - The raw restaurant ID from router.query (can be string | string[] | undefined)
 * @returns An object with:
 *   - isValid: boolean indicating if the ID is valid
 *   - restaurantId: the normalized numeric ID (only valid if isValid is true)
 *   - error: error message if validation failed
 *
 * @example
 * ```typescript
 * const result = validateRestaurantId(router.query.id)
 * if (!result.isValid) {
 *   router.replace(ROUTES.UNAUTHORIZED)
 *   return
 * }
 * const id = result.restaurantId // Safe to use
 * ```
 */
export const validateRestaurantId = (
  restaurantIdRaw: string | string[] | undefined
): { isValid: boolean; restaurantId?: number; error?: string } => {
  // Handle array case (take first element)
  const restaurantId = Array.isArray(restaurantIdRaw) ? restaurantIdRaw[0] : restaurantIdRaw

  // Check if ID exists and is a string
  if (!restaurantId || typeof restaurantId !== 'string') {
    return {
      isValid: false,
      error: 'Restaurant ID is missing or invalid'
    }
  }

  // Strict validation: must be a string containing only digits
  // This prevents bypass attempts with non-numeric or partially numeric IDs
  if (!/^\d+$/.test(restaurantId)) {
    return {
      isValid: false,
      error: 'Restaurant ID must be a valid numeric string'
    }
  }

  const restaurantIdNum = Number(restaurantId)

  return {
    isValid: true,
    restaurantId: restaurantIdNum
  }
}
